import { FieldHint } from "@/components/field-hint/FieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Button,
  Input,
  Select,
  Stack,
  Textarea,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { useRecent } from "@/hooks/use-recent.hook.ts";
import { ApiRecentKey } from "@/types/recents/recents.types.ts";
import { useEmployeeAdditionalCompensationSave } from "@/queries/employees/use-employee-additional-compensations.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useCompensationComponents } from "@/queries/use-compensation-components.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployeeAdditionalCompensation } from "@/types/employees/employee-additional-compensation.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { buildEmployeeAdditionalCompensationSchema } from "@/pages/employees/employee/widgets/compensation/employee-additional-compensation-schema.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { ApiFieldTypes, ApiTableTypes } from "@/types/custom-field.types.ts";

export interface EmployeeAdditionalCompensationEditModalProps extends ModalProps {
  employee: ApiEmployee;
  employeeAdditionalCompensation?: ApiEmployeeAdditionalCompensation;
  lastCurrency?: ApiEmployeeAdditionalCompensation["currency"];
}

type EmployeeAdditionalCompensationFormValues = {
  compensation_type_id?: number | null;
  description?: string | null;
  date_effective_from: Date;
  amount_gross?: string | number | null;
  amount_net?: string | number | null;
  currency?: string | null;
  comment?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & Record<string, any>;

export function EmployeeAdditionalCompensationEditModal({
  employee,
  employeeAdditionalCompensation,
  lastCurrency,
  ...props
}: EmployeeAdditionalCompensationEditModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { id } = { ...props };
  const { close } = useModals();
  const { fields, customFields, isMandatory } = useSystemTableConfig(
    ApiTableTypes.employee_additional_compensations
  );
  const {
    getFieldByType,
    getValueForFieldFromValueObject,
    getZodSchemaForField,
    transformFormDataToValue,
  } = useCustomFieldsHook();
  const fieldConfig = (type: ApiFieldTypes) => fields.find((field) => field.type === type);
  const fieldOrder = (type: ApiFieldTypes) => fields.findIndex((field) => field.type === type);
  const fieldTitle = (type: ApiFieldTypes, fallback: string) =>
    fieldConfig(type)?.title?.trim() || fallback;

  const { data: employeeMe } = useEmployeeMe();

  const SchemaObject = buildEmployeeAdditionalCompensationSchema({
    isMandatory,
    hasField: (type) => Boolean(fieldConfig(type)),
    customFields,
    getZodSchemaForField,
  });

  const { data: compensationTypes, isLoading: compensationTypesIsLoading } =
    useCompensationComponents();

  const formValues = useMemo(
    () => ({
      compensation_type_id: employeeAdditionalCompensation?.compensation_type.id ?? undefined,
      description: employeeAdditionalCompensation?.description ?? null,
      date_effective_from: employeeAdditionalCompensation?.date_effective_from,
      amount_gross:
        employeeAdditionalCompensation?.amount_gross == null
          ? null
          : Number(employeeAdditionalCompensation.amount_gross),
      amount_net:
        employeeAdditionalCompensation?.amount_net == null
          ? null
          : Number(employeeAdditionalCompensation.amount_net),
      currency:
        employeeAdditionalCompensation?.currency ??
        lastCurrency ??
        employeeMe?.organization.currency ??
        undefined,
      comment: employeeAdditionalCompensation?.comment ?? null,
      ...Object.assign(
        {},
        ...customFields.map((field) => {
          const value = employeeAdditionalCompensation?.custom_fields?.find(
            (item) => item.field.id === field.id
          );
          return getValueForFieldFromValueObject(field, value?.value, true);
        })
      ),
    }),
    [
      customFields,
      employeeAdditionalCompensation,
      employeeMe?.organization.currency,
      getValueForFieldFromValueObject,
      lastCurrency,
    ]
  );

  const { addRecent } = useRecent();

  const formHook = useForm<EmployeeAdditionalCompensationFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(
      SchemaObject
    ) as unknown as Resolver<EmployeeAdditionalCompensationFormValues>,
    values: formValues,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, dirtyFields },
  } = formHook;

  const employeeAdditionalCompensationSave = useEmployeeAdditionalCompensationSave(control);

  const onSubmit = handleSubmit(async (schemaData) => {
    try {
      const result = await employeeAdditionalCompensationSave.mutateAsync({
        employeeId: employee.id,
        employeeAdditionalCompensationId: employeeAdditionalCompensation?.id,
        payload: {
          date_effective_from: schemaData.date_effective_from,
          compensation_type_id: schemaData.compensation_type_id as number,
          description: schemaData.description ?? null,
          amount_gross: schemaData.amount_gross == null ? null : String(schemaData.amount_gross),
          amount_net: schemaData.amount_net == null ? null : String(schemaData.amount_net),
          currency: schemaData.currency ?? null,
          comment: schemaData.comment ?? null,
          custom_fields: Object.fromEntries(
            customFields.map((field) => [field.slug, transformFormDataToValue(field, schemaData)])
          ),
        },
      });

      if (result) {
        if (dirtyFields.currency && schemaData.currency) {
          addRecent([{ type: ApiRecentKey.currency, values: [schemaData.currency] }]);
        }
        if (employeeAdditionalCompensation) {
          analyticsService.trackEvent(analyticEvents.employeeCompensations.additionalEdited, {
            [analyticProperties.id]: employeeAdditionalCompensation.id,
          });
        } else {
          analyticsService.trackEvent(analyticEvents.employeeCompensations.additionalCreated);
        }
        close(id);
      }
    } catch {
      return;
    }
  });

  return (
    <Modal
      title={t("core:additional_compensation")}
      caption={renderEmployeeName(employee)}
      variant={"side"}
      layout={"stretch"}
      withCloser={true}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={employeeAdditionalCompensationSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
      {...props}
    >
      <Stack>
        <>
          <Title header={t("common:details")} paddingTop={false} paddingBottom size={"xs"} />
          <form>
            <Stack>
              {fieldConfig(ApiFieldTypes.effective_from) && (
                <Stack style={{ order: fieldOrder(ApiFieldTypes.effective_from) }} gap="xs">
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value}
                        onChange={onChange}
                        required={true}
                        label={fieldTitle(
                          ApiFieldTypes.effective_from,
                          t("common:date_effective_from")
                        )}
                        error={errors.date_effective_from?.message as string | undefined}
                      />
                    )}
                    control={control}
                    name={"date_effective_from"}
                  />
                  <FieldHint hint={fieldConfig(ApiFieldTypes.effective_from)?.hint} />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_description) && (
                <Stack
                  style={{
                    order: fieldOrder(ApiFieldTypes.employee_additional_compensation_description),
                  }}
                  gap="xs"
                >
                  <Textarea
                    label={fieldTitle(
                      ApiFieldTypes.employee_additional_compensation_description,
                      t("common:description")
                    )}
                    required={isMandatory(
                      ApiFieldTypes.employee_additional_compensation_description
                    )}
                    error={errors.description?.message as string | undefined}
                    {...register("description")}
                  />
                  <FieldHint
                    hint={
                      fieldConfig(ApiFieldTypes.employee_additional_compensation_description)?.hint
                    }
                  />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_compensation_type) && (
                <Stack
                  style={{
                    order: fieldOrder(
                      ApiFieldTypes.employee_additional_compensation_compensation_type
                    ),
                  }}
                  gap="xs"
                >
                  <Controller
                    control={control}
                    name={`compensation_type_id`}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        label={fieldTitle(
                          ApiFieldTypes.employee_additional_compensation_compensation_type,
                          t("core:compensation_type")
                        )}
                        required={isMandatory(
                          ApiFieldTypes.employee_additional_compensation_compensation_type
                        )}
                        options={compensationTypes || []}
                        isLoading={compensationTypesIsLoading}
                        emptyTitle={t("common:select")}
                        value={value}
                        searchable={true}
                        isClearable={true}
                        onChange={onChange}
                        error={errors.compensation_type_id?.message as string | undefined}
                      />
                    )}
                  />
                  <FieldHint
                    hint={
                      fieldConfig(ApiFieldTypes.employee_additional_compensation_compensation_type)
                        ?.hint
                    }
                  />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_amount_gross) && (
                <Stack
                  style={{
                    order: fieldOrder(ApiFieldTypes.employee_additional_compensation_amount_gross),
                  }}
                  gap="xs"
                >
                  <Controller
                    control={control}
                    name="amount_gross"
                    render={({ field: { value, onChange } }) => (
                      <Input
                        value={String(value ?? "")}
                        type="text"
                        onChange={onChange}
                        inputMode="decimal"
                        label={fieldTitle(
                          ApiFieldTypes.employee_additional_compensation_amount_gross,
                          t("core:amount_money_gross")
                        )}
                        error={errors.amount_gross?.message as string | undefined}
                      />
                    )}
                  />
                  <FieldHint
                    hint={
                      fieldConfig(ApiFieldTypes.employee_additional_compensation_amount_gross)?.hint
                    }
                  />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_amount_net) && (
                <Stack
                  style={{
                    order: fieldOrder(ApiFieldTypes.employee_additional_compensation_amount_net),
                  }}
                  gap="xs"
                >
                  <Controller
                    control={control}
                    name="amount_net"
                    render={({ field: { value, onChange } }) => (
                      <Input
                        value={String(value ?? "")}
                        type="text"
                        onChange={onChange}
                        inputMode="decimal"
                        label={fieldTitle(
                          ApiFieldTypes.employee_additional_compensation_amount_net,
                          t("core:amount_money_net")
                        )}
                        error={errors.amount_net?.message as string | undefined}
                      />
                    )}
                  />
                  <FieldHint
                    hint={
                      fieldConfig(ApiFieldTypes.employee_additional_compensation_amount_net)?.hint
                    }
                  />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_currency) && (
                <Stack
                  style={{
                    order: fieldOrder(ApiFieldTypes.employee_additional_compensation_currency),
                  }}
                  gap="xs"
                >
                  <Controller
                    control={control}
                    name={`currency`}
                    render={({ field: { value, onChange } }) => (
                      <CurrencySelect
                        label={fieldTitle(
                          ApiFieldTypes.employee_additional_compensation_currency,
                          t("common:currency")
                        )}
                        required={isMandatory(
                          ApiFieldTypes.employee_additional_compensation_currency
                        )}
                        emptyTitle={t("common:select")}
                        value={value}
                        searchable={true}
                        isClearable={true}
                        onChange={onChange}
                        error={errors.currency?.message as string | undefined}
                      />
                    )}
                  />
                  <FieldHint
                    hint={
                      fieldConfig(ApiFieldTypes.employee_additional_compensation_currency)?.hint
                    }
                  />
                </Stack>
              )}
              {fieldConfig(ApiFieldTypes.employee_additional_compensation_comment) && (
                <Stack
                  style={{
                    order: fieldOrder(ApiFieldTypes.employee_additional_compensation_comment),
                  }}
                  gap="xs"
                >
                  <Textarea
                    label={fieldTitle(
                      ApiFieldTypes.employee_additional_compensation_comment,
                      t("common:add_comment")
                    )}
                    required={isMandatory(ApiFieldTypes.employee_additional_compensation_comment)}
                    error={errors.comment?.message as string | undefined}
                    {...register("comment")}
                  />
                  <FieldHint
                    hint={fieldConfig(ApiFieldTypes.employee_additional_compensation_comment)?.hint}
                  />
                </Stack>
              )}
              {customFields.map((field) => (
                <Stack
                  key={field.id}
                  style={{ order: fields.findIndex((item) => item.id === field.id) }}
                >
                  {getFieldByType({
                    field,
                    formHook,
                    canView: true,
                    canEdit: true,
                    label: field.title,
                    options: field.options.map((option) => ({
                      id: Number(option.id),
                      title: option.title,
                    })),
                  })}
                </Stack>
              ))}
            </Stack>
          </form>
        </>
      </Stack>
    </Modal>
  );
}
